import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ReceiptFormGroup } from '@app/v2/components/receipt/receipt-edit/receipt-edit.formgroup';
import { RentalCarPreferencesFormGroup } from '@components/molecules/forms/patient-travel-card/car-rental-preferences/car-rental-preferences.component';
import { FlightPreferencesFormGroup } from '@components/molecules/forms/patient-travel-card/flight-preferences/flight-preferences.component';
import { LodgingPreferencesFormGroup } from '@components/molecules/forms/patient-travel-card/lodging-preferences/lodging-preferences.component';
import { TrainPreferencesFormGroup } from '@components/molecules/forms/patient-travel-card/train-preferences/train-preferences.component';
import { CarServicePreferencesFormGroup } from '@components/molecules/forms/patient-travel-card/transfer-preferences/transfer-preferences.component';
import { Caregiver } from '@models/patient';
import { PersistentFormControl, PersistentFormGroup } from '@utility/persistent-forms';
import { FormGroupValueOf } from '@utility/utility';
import { CarServiceRequestDetailsFormGroup } from './detail-form-groups/travel/car-service-request.formgroup';
import { FlightRequestDetailsFormGroup } from './detail-form-groups/travel/flight-request.formgroup';
import { LodgingRequestDetailsFormGroup } from './detail-form-groups/travel/lodging-request.formgroup';
import { OtherRequestDetailsFormGroup } from './detail-form-groups/travel/other-request.formgroup';
import { RentalCarRequestDetailsFormGroup } from './detail-form-groups/travel/rental-car-request.formgroup';
import { TrainRequestDetailsFormGroup } from './detail-form-groups/travel/train-request.formgroup';

export interface BookableCaregiver {
    caregiver: Caregiver;
    bookable: boolean;
}
export class CheckboxFormGroup<T> extends PersistentFormGroup<{
    checked: FormControl<boolean | null>;
    value: FormControl<T>;
}> {}

export class NeedsTravelFormGroup<T extends FormGroup> extends FormGroup<{
    needsTravel: PersistentFormControl<boolean | null>;
    travel: T;
}> {}

export class TravelFormGroup<
    TPreferencesFormGroup extends FormGroup,
    TRequestDetailsFormGroup extends FormGroup,
> extends FormGroup<{
    preferences: TPreferencesFormGroup;
    requestDetails: TRequestDetailsFormGroup;
}> {}

export class NoPreferenceTravelFormGroup<TRequestDetailsFormGroup extends FormGroup> extends FormGroup<{
    requestDetails: TRequestDetailsFormGroup;
}> {}

export class AccompanyingCaregiversFormGroup extends PersistentFormGroup<{
    primaryCaregiver: CheckboxFormGroup<BookableCaregiver | null>;
    otherCaregivers: FormArray<CheckboxFormGroup<BookableCaregiver | null>>;
    none: CheckboxFormGroup<null>;
}> {}

export class TravelRequestCardFormGroup extends FormGroup<{
    accompanyingCaregivers: AccompanyingCaregiversFormGroup;
    travelDetails: FormGroup<{
        flight: NeedsTravelFormGroup<TravelFormGroup<FlightPreferencesFormGroup, FlightRequestDetailsFormGroup>>;
        train: NeedsTravelFormGroup<TravelFormGroup<TrainPreferencesFormGroup, TrainRequestDetailsFormGroup>>;
        lodging: NeedsTravelFormGroup<TravelFormGroup<LodgingPreferencesFormGroup, LodgingRequestDetailsFormGroup>>;
        rentalCar: NeedsTravelFormGroup<
            TravelFormGroup<RentalCarPreferencesFormGroup, RentalCarRequestDetailsFormGroup>
        >;
        carService: NeedsTravelFormGroup<
            TravelFormGroup<CarServicePreferencesFormGroup, CarServiceRequestDetailsFormGroup>
        >;
        other: NeedsTravelFormGroup<NoPreferenceTravelFormGroup<OtherRequestDetailsFormGroup>>;
    }>;
    receipts: FormArray<ReceiptFormGroup>;
}> {
    public static create(value: FormGroupValueOf<TravelRequestCardFormGroup>): TravelRequestCardFormGroup {
        return new TravelRequestCardFormGroup({
            accompanyingCaregivers: new PersistentFormGroup(
                {
                    primaryCaregiver: new CheckboxFormGroup({
                        checked: new FormControl(value.accompanyingCaregivers.primaryCaregiver.checked),
                        value: new FormControl(value.accompanyingCaregivers.primaryCaregiver.value),
                    }),
                    otherCaregivers: new FormArray(
                        value.accompanyingCaregivers.otherCaregivers.map(
                            (caregiver) =>
                                new CheckboxFormGroup({
                                    checked: new FormControl(caregiver.checked),
                                    value: new FormControl(caregiver.value),
                                })
                        )
                    ),
                    none: new CheckboxFormGroup({
                        checked: new FormControl(value.accompanyingCaregivers.none.checked),
                        value: new FormControl(value.accompanyingCaregivers.none.value),
                    }),
                },
                (control: AbstractControl): ValidationErrors | null => {
                    const typedControl = control as TravelRequestCardFormGroup['controls']['accompanyingCaregivers'];
                    const value = typedControl.getRawValue();
                    if ([value.primaryCaregiver, ...value.otherCaregivers, value.none].some((v) => v.checked))
                        return null;
                    else return { requiredAtLeastOne: 'At least one option is required' };
                }
            ),
            travelDetails: new FormGroup(
                {
                    flight: new FormGroup({
                        needsTravel: new PersistentFormControl(value.travelDetails.flight.needsTravel, [
                            Validators.required,
                        ]),
                        travel: new FormGroup({
                            preferences: FlightPreferencesFormGroup.create(
                                value.travelDetails.flight.travel.preferences
                            ),
                            requestDetails: FlightRequestDetailsFormGroup.create(
                                value.travelDetails.flight.travel.requestDetails
                            ),
                        }),
                    }),
                    train: new FormGroup({
                        needsTravel: new PersistentFormControl(value.travelDetails.train.needsTravel, [
                            Validators.required,
                        ]),
                        travel: new FormGroup({
                            preferences: TrainPreferencesFormGroup.create(value.travelDetails.train.travel.preferences),
                            requestDetails: TrainRequestDetailsFormGroup.create(
                                value.travelDetails.train.travel.requestDetails
                            ),
                        }),
                    }),
                    lodging: new FormGroup({
                        needsTravel: new PersistentFormControl(value.travelDetails.lodging.needsTravel, [
                            Validators.required,
                        ]),
                        travel: new FormGroup({
                            preferences: LodgingPreferencesFormGroup.create(
                                value.travelDetails.lodging.travel.preferences
                            ),
                            requestDetails: LodgingRequestDetailsFormGroup.create(
                                value.travelDetails.lodging.travel.requestDetails
                            ),
                        }),
                    }),
                    rentalCar: new FormGroup({
                        needsTravel: new PersistentFormControl(value.travelDetails.rentalCar.needsTravel, [
                            Validators.required,
                        ]),
                        travel: new FormGroup({
                            preferences: RentalCarPreferencesFormGroup.create(
                                value.travelDetails.rentalCar.travel.preferences
                            ),
                            requestDetails: RentalCarRequestDetailsFormGroup.create(
                                value.travelDetails.rentalCar.travel.requestDetails
                            ),
                        }),
                    }),
                    carService: new FormGroup({
                        needsTravel: new PersistentFormControl(value.travelDetails.carService.needsTravel, [
                            Validators.required,
                        ]),
                        travel: new FormGroup({
                            preferences: CarServicePreferencesFormGroup.create(
                                value.travelDetails.carService.travel.preferences
                            ),
                            requestDetails: CarServiceRequestDetailsFormGroup.create(
                                value.travelDetails.carService.travel.requestDetails
                            ),
                        }),
                    }),
                    other: new FormGroup({
                        needsTravel: new PersistentFormControl(value.travelDetails.other.needsTravel, [
                            Validators.required,
                        ]),
                        travel: new FormGroup({
                            requestDetails: OtherRequestDetailsFormGroup.create(
                                value.travelDetails.other.travel.requestDetails
                            ),
                        }),
                    }),
                },
                [
                    (control: AbstractControl): ValidationErrors | null => {
                        const typedControl = control as TravelRequestCardFormGroup['controls']['travelDetails'];
                        const value = typedControl.getRawValue();
                        if (
                            value.flight.needsTravel ||
                            value.train.needsTravel ||
                            value.lodging.needsTravel ||
                            value.rentalCar.needsTravel ||
                            value.carService.needsTravel ||
                            value.other.needsTravel
                        )
                            return null;
                        else {
                            return { requiredAtLeastOneTravelOption: 'At least one travel option is required.' };
                        }
                    },
                    (control: AbstractControl): ValidationErrors | null => {
                        const typedControl = control as TravelRequestCardFormGroup['controls']['travelDetails'];
                        const value = typedControl.getRawValue();

                        const flightFormGroup = typedControl.controls.flight.controls.travel.controls;
                        const flightTabInvalid =
                            value.flight.needsTravel &&
                            (flightFormGroup.requestDetails.invalid || flightFormGroup.preferences.invalid);

                        const trainFormGroup = typedControl.controls.train.controls.travel.controls;
                        const trainTabInvalid =
                            value.train.needsTravel &&
                            (trainFormGroup.requestDetails.invalid || trainFormGroup.preferences.invalid);

                        const lodgingFormGroup = typedControl.controls.lodging.controls.travel.controls;
                        const lodgingTabInvalid =
                            value.lodging.needsTravel &&
                            (lodgingFormGroup.requestDetails.invalid || lodgingFormGroup.preferences.invalid);

                        const rentalCarFormGroup = typedControl.controls.rentalCar.controls.travel.controls;
                        const rentalCarTabInvalid =
                            value.rentalCar.needsTravel &&
                            (rentalCarFormGroup.requestDetails.invalid || rentalCarFormGroup.preferences.invalid);

                        const carServiceFormGroup = typedControl.controls.carService.controls.travel.controls;
                        const carServiceTabInvalid =
                            value.carService.needsTravel &&
                            (carServiceFormGroup.requestDetails.invalid || carServiceFormGroup.preferences.invalid);

                        const otherServiceFormGroup = typedControl.controls.other.controls.travel.controls;
                        const otherTabInvalid = value.other.needsTravel && otherServiceFormGroup.requestDetails.invalid;

                        if (
                            flightTabInvalid ||
                            trainTabInvalid ||
                            lodgingTabInvalid ||
                            rentalCarTabInvalid ||
                            carServiceTabInvalid ||
                            otherTabInvalid
                        ) {
                            const invalidTabsNames: string[] = [];
                            if (flightTabInvalid) invalidTabsNames.push('Flight');
                            if (trainTabInvalid) invalidTabsNames.push('Train');
                            if (lodgingTabInvalid) invalidTabsNames.push('Lodging');
                            if (rentalCarTabInvalid) invalidTabsNames.push('Rental Car');
                            if (carServiceTabInvalid) invalidTabsNames.push('Car Service');
                            if (otherTabInvalid) invalidTabsNames.push('Other');

                            return {
                                travelInfoMissing: `Travel information missing in tabs: ${invalidTabsNames.join(', ')}. Please make sure that all tabs are filled out correctly `,
                            };
                        } else {
                            return null;
                        }
                    },
                ]
            ),
            receipts: new FormArray<ReceiptFormGroup>([]),
        });
    }
}
